import {
  HStack,
  ModalCloseButton,
  Spacer,
  Text,
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Box,
  Button,
  ButtonGroup
} from '@chakra-ui/react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useEffect, useState } from 'react';

import DigestForm from '@components/digest/digest-form.component';
import StepWithLabelComponent from '@components/general/form/step-with-label.component';
import CustomSelect from '@components/general/form/custom-select.component';
import InfoBlurb from '@components/general/info-blurb.component';

import { useUserStore } from '@store/app.store';
import { connectZoomAccount } from '@services/zoom.service';
import { useZoomChannels, processChannelById } from '@services/query-services/lambda.query-service';
import {
  convertChannelToFormValues,
  convertFormValuesToChannel,
  toSelectOptions
} from '@util/helpers';
import { useCreateDigest, useDigestModalConfigVals, useEditDigest } from '@util/hooks';
import { SFBoxWithPencil, SFPlusCircleIcon, ZoomLogoIcon } from '@assets/icons';

const defaultValues = { cadenceInterval: 'daily', identifiers: [] };

export default function ZoomDigestModal({ disclosure, digest, isEdit }) {
  const [hasInitializedEditVals, setHasInitializedEditVals] = useState(false);
  const user = useUserStore((state) => state?.user);
  const { userTz, tenantTz, userHasAccess } = useDigestModalConfigVals('Zoom');

  const { data: allZoomChannels } = useZoomChannels(user?.id, userHasAccess);

  const { isPatching, onPatch } = useEditDigest('Zoom', digest, () => disclosure.onClose());
  const { isCreating, onCreate } = useCreateDigest('Zoom', () => disclosure.onClose());

  const form = useForm({ defaultValues: digest ?? defaultValues });

  useEffect(() => {
    // reset form prefilling when new digest passed
    if (isEdit) {
      setHasInitializedEditVals(false);
    }
  }, [digest?.id, isEdit]);

  useEffect(() => {
    // populate all existing values when digest is fetched
    if (isEdit && !hasInitializedEditVals && digest && allZoomChannels) {
      const channelIdentifiers = new Set(digest.identifiers);

      form.reset({
        ...convertChannelToFormValues(digest),
        identifiers: toSelectOptions(
          'id',
          'name',
          true
        )(allZoomChannels).filter((chOption) => channelIdentifiers.has(chOption.value))
      });
      setHasInitializedEditVals(true);
    }
  }, [isEdit, hasInitializedEditVals, digest, allZoomChannels, form.reset]);

  useEffect(() => {
    form.reset(defaultValues);
  }, [form.formState.isSubmitSuccessful]);

  const onSubmit = async (formValues) => {
    const action = isEdit ? onPatch : onCreate;
    const channelBody = convertFormValuesToChannel(formValues, userTz ?? tenantTz, !isEdit);
    const channel = await action(channelBody);
    if (!isEdit) await processChannelById(channel?.id);
  };

  return (
    <Modal
      {...disclosure}
      variant='classic'
      size='2xl'
      onClose={() => {
        setHasInitializedEditVals(false);
        disclosure.onClose();
        form.reset(defaultValues);
      }}
    >
      <ModalOverlay />
      <ModalContent minH={{ base: 'unset', lg: 'fit-content' }}>
        <ModalHeader>
          <HStack>
            {isEdit ? (
              <SFBoxWithPencil color='brandPrimary.onPrimary' />
            ) : (
              <SFPlusCircleIcon
                color='brandPrimary.onPrimary'
                boxSize={7}
                p={1}
              />
            )}

            <Text variant='body-600-md'>{isEdit ? 'Edit' : 'Create a'} Zoom Digest</Text>
            <Spacer />
            <ModalCloseButton rounded='full' />
          </HStack>
        </ModalHeader>
        <ModalBody>
          {userHasAccess ? (
            <FormProvider {...form}>
              <DigestForm
                isEdit={isEdit}
                sourceType='Zoom'
                parentDisclosure={disclosure}
                isSubmitting={isCreating || isPatching}
                onSubmit={onSubmit}
              >
                <Controller
                  control={form.control}
                  name='identifiers'
                  rules={{ required: 'At least one channel is required' }}
                  render={({ field: { name, value, onChange }, fieldState }) => (
                    <StepWithLabelComponent
                      name={name}
                      fieldState={fieldState}
                      label='Which channels do you want to summarize?'
                    >
                      <CustomSelect
                        size='md'
                        getOptionLabel={(opt) => opt?.name}
                        getOptionValue={(opt) => opt?.id}
                        placeholder='Enter Zoom channel names'
                        value={value}
                        onChange={onChange}
                        options={allZoomChannels}
                      />
                    </StepWithLabelComponent>
                  )}
                />
              </DigestForm>
            </FormProvider>
          ) : (
            <Box p={6}>
              <InfoBlurb
                title='Connect Zoom'
                icon={<ZoomLogoIcon boxSize={10} />}
                iconWrapperProps={{
                  bgColor: 'surface.containerLowest',
                  borderRadius: 'xl'
                }}
                onCloseClick={() => disclosure.onClose()}
                footer={
                  <ButtonGroup alignSelf='flex-end'>
                    <Button
                      size='reskinMd'
                      variant='reskinWhite'
                      border='none'
                      as={ReactRouterLink}
                      to='https://intercom.help/fora/en/collections/9459341-fora-knowledge-base'
                      target='_blank'
                    >
                      Learn More
                    </Button>
                    <Button
                      size='reskinMd'
                      data-testid='connect-zoom-button'
                      onClick={() => connectZoomAccount(false)}
                    >
                      Connect Zoom
                    </Button>
                  </ButtonGroup>
                }
              >
                Connect your Zoom account to create Zoom Digests of your channels.
              </InfoBlurb>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
